import React, { useEffect, useRef, useState } from "react";
import { useStyles } from "./SearchPage.styles";
import { Checkbox, IconButton, PrimaryButton, SearchBox, Toggle } from "@fluentui/react";
import { IdGroup, SearchItemList } from "@v2/components/controls/PickerList/search/SearchItemList/SearchItemList";
import { useScrollClip } from "@v2/components/controls/PickerList/search/hooks";

interface SearchPageProps {
  isOpen: boolean;
  items: any[];
  selectedIds: string[];
  onSelectionChange: (itemId: string[]) => void;
  idGroups?: IdGroup[];
  title?: string;
  onApply: (ids: string[]) => void;
  onDismiss: () => void;
}

export const SearchPage: React.FC<SearchPageProps> = (props) => {

  const {title, isOpen, onDismiss, items, selectedIds, idGroups = [], onApply} = props;
  const styles = useStyles();
  const [idsToApply, setIdsToApply] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('')
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  useScrollClip(isOpen);

  useEffect(() => {
    setIdsToApply(selectedIds);
  }, [selectedIds, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      rootRef.current?.classList.remove('appeared');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isVisible) {
      rootRef.current?.classList.add('appeared');
    }
  }, [isVisible]);

  const handleClickBack = () => {
    onDismiss();
  }

  const handleItemSelected = (itemId: string, selected: boolean) => {
    selected
      ? setIdsToApply([...idsToApply, itemId])
      : setIdsToApply(idsToApply.filter(id => id !== itemId));
  }

  const handleClickApply = () => {
    selectedIds !== idsToApply && onApply(idsToApply);
    onDismiss();
  }

  const handleClickSelectAll = (checked: boolean) => {
    if (checked) {
      setIdsToApply(items.map(item => item.id));
    } else {
      setIdsToApply([]);
    }
  }

  const rootRef = useRef<HTMLDivElement>(null);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={styles.modalPage}
      ref={rootRef}
      onTransitionEnd={() => {
        !isOpen && setIsVisible(false)
      }}
    >
      <div className={styles.header}>
        <IconButton
          iconProps={{iconName: 'ChevronLeftMed'}}
          onClick={handleClickBack}
        />
        {title || 'Select Items'}
      </div>
      <div className={styles.topPanel}>
        <div className='select-all'>
          <Checkbox
            indeterminate={!!idsToApply.length && items.length > idsToApply.length}
            checked={items.length === idsToApply.length}
            onChange={(_, checked) => handleClickSelectAll(!!checked)}
          />
          <IconButton
            menuProps={{
              items: idGroups.map((group, index) => ({
                key: group.label,
                text: group.label,
                onClick: () => setIdsToApply(idGroups[index].ids),
              }))
            }}
          />
        </div>
        <div style={{width: '100%'}}>
          <SearchBox
            iconProps={{iconName: 'search'}}
            ref={searchBoxRef}
            value={searchValue}
            onChange={(_, newValue) => setSearchValue(newValue || '')}
            placeholder={'Type name to search'}
            styles={{
              root: {flexShrink: 0, margin: '0 4px', width: '100%'}
            }}
          />
        </div>
      </div>
      <SearchItemList
        items={items}
        selectedIds={idsToApply}
        showOnlySelected={showOnlySelected}
        onItemSelected={handleItemSelected}
        searchValue={searchValue}
        idGroups={idGroups}
      />
      <div className={styles.bottomPanel}>
        <div className='toggleContainer'>
          <Toggle
            checked={showOnlySelected}
            onChange={(_, checked) => setShowOnlySelected(!!checked)}
          />
          <span className='controlLabel'>only selected</span>
        </div>
        <PrimaryButton
          className='applyButton'
          onClick={handleClickApply}
        >
          Apply
        </PrimaryButton>
      </div>
    </div>
  )
}
